import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { initializeApp, getApps, getApp } from "firebase/app";
import { firebaseConfig } from "../utility/firebaseConfig";
import { post } from "../utility/fetch";
import { NotificationsContext } from "./NotificationsContext";
import toast from 'react-hot-toast';

export const FirebaseMessagingContext = createContext();

export const FirebaseMessagingProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [initError, setInitError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [fcmToken, setFcmToken] = useState(null);
  const { refreshNotifications } = useContext(NotificationsContext) || {};

  // Load unread count from localStorage on mount
  useEffect(() => {
    const savedCount = localStorage.getItem('fcm_unread_count');
    if (savedCount) {
      setUnreadCount(parseInt(savedCount, 10));
    }
  }, []);

  // Save unread count to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('fcm_unread_count', unreadCount.toString());
  }, [unreadCount]);

  const initializeFirebaseMessaging = useCallback(async () => {
    try {
      // Check if user is logged in
      const userId = sessionStorage.getItem("userId");
      if (!userId) {
        console.log('⏸️ User not logged in, skipping Firebase initialization');
        return;
      }

      // Validate Firebase config
      if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
        throw new Error('Firebase configuration is incomplete');
      }

      // Check browser support
      if (!('Notification' in window)) {
        console.warn('⚠️ This browser does not support notifications');
        return;
      }

      if (!('serviceWorker' in navigator)) {
        console.warn('⚠️ This browser does not support service workers');
        return;
      }

      console.log('🚀 Initializing Firebase messaging...');

      // Initialize Firebase app (singleton pattern)
      let app;
      if (getApps().length === 0) {
        app = initializeApp(firebaseConfig);
        console.log('✅ Firebase app initialized');
      } else {
        app = getApp();
        console.log('✅ Using existing Firebase app');
      }

      // Register service worker first
      await registerServiceWorker();

      // Initialize messaging
      const messaging = getMessaging(app);
      console.log('✅ Firebase messaging instance created');

      // Get and send FCM token
      const token = await getAndSendToken(messaging);
      if (token) {
        setFcmToken(token);
      }

      // Setup message listener
      setupMessageListener(messaging);

      setIsInitialized(true);
      console.log('✅ Firebase messaging fully initialized');

    } catch (error) {
      console.error('❌ Error initializing Firebase messaging:', error);
    }
  }, []);

  useEffect(() => {
    initializeFirebaseMessaging();
  }, [initializeFirebaseMessaging]);

  const registerServiceWorker = async () => {
    try {
      // Check if service worker is already registered
      const existingRegistration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
      
      if (existingRegistration) {
        console.log('✅ Service worker already registered');
        return existingRegistration;
      }

      // Register new service worker
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/'
      });

      console.log('✅ Service worker registered successfully');

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      console.log('✅ Service worker is ready');

      return registration;
    } catch (error) {
      console.error('❌ Service worker registration failed:', error);
    }
  };

  const getAndSendToken = async (messaging) => {
    try {
      // Check current permission
      let permission = Notification.permission;
      
      if (permission === 'default') {
        console.log('🔔 Requesting notification permission...');
        permission = await Notification.requestPermission();
      }

      if (permission !== 'granted') {
        console.warn('⚠️ Notification permission not granted:', permission);
        setInitError('Notification permission denied');
        return null;
      }

      console.log('✅ Notification permission granted');

      // Get VAPID key
      const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
      
      if (!vapidKey) {
        throw new Error('VAPID key not found in environment variables');
      }

      console.log('🔑 Getting FCM token...');
      
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Get FCM token
      const token = await getToken(messaging, {
        vapidKey: vapidKey,
        serviceWorkerRegistration: registration
      });

      if (token) {
        console.log("✅ FCM Token obtained:", token.substring(0, 30) + '...');

        // Send token to backend
        const userId = sessionStorage.getItem("userId");
        
        if (userId) {
          try {
            await post("/auth/update-device-token", {
              userId: parseInt(userId),
              deviceToken: token,
            });
            console.log("✅ Device token sent to backend");
          } catch (error) {
            console.error("❌ Failed to send token to backend:", error);
          }
        }

        return token;
      } else {
        console.warn("⚠️ No FCM token available");
        setInitError('Failed to get FCM token');
        return null;
      }
    } catch (err) {
      console.error("❌ Error getting/sending FCM token:", err);
      
      // Provide more specific error messages
      if (err.code === 'messaging/permission-blocked') {
        setInitError('Notification permission is blocked. Please enable it in browser settings.');
      } else if (err.code === 'messaging/token-subscribe-failed') {
        setInitError('Failed to subscribe to notifications. Please try again.');
      } else {
        setInitError(err.message);
      }
      
      return null;
    }
  };

  const setupMessageListener = (messaging) => {
    try {
      // Foreground message listener
      const unsubscribe = onMessage(messaging, (payload) => {
        console.log("📨 Foreground message received:", payload);
        
        // Handle call notifications differently
        if (payload.data?.type === 'incoming_call') {
          handleCallNotification(payload);
          return;
        }

        try {
          // Create notification object
          const notification = {
            id: payload.messageId || Date.now().toString(),
            title: payload.notification?.title || 'New Message',
            body: payload.notification?.body || 'You have a new message',
            icon: payload.notification?.icon || '/ConnectHealthPro.png',
            data: payload.data || {},
            timestamp: payload.sentTime || Date.now(),
            read: false
          };

          // Update messages state and increment unread count atomically
          setMessages(prev => {
            const newMessages = [notification, ...prev];
            setUnreadCount(current => {
              const newCount = current + 1;
              localStorage.setItem('fcm_unread_count', newCount.toString());
              return newCount;
            });
            return newMessages;
          });

          // Refresh notifications if available
          if (refreshNotifications) {
            refreshNotifications();
          }

          // Show toast for regular messages
          toast.success(`${notification.title}: ${notification.body}`, {
            duration: 5000,
            icon: '🔔',
          });

          // Show browser notification for foreground messages (NO ACTIONS)
          if (Notification.permission === 'granted') {
            const notificationOptions = {
              body: notification.body,
              icon: notification.icon,
              badge: '/ConnectHealthPro.png',
              tag: payload.messageId || 'foreground-notification',
              data: payload.data || {},
              requireInteraction: false,
              timestamp: Date.now(),
              silent: false,
              // DO NOT ADD actions here - only works in service worker
            };

            const browserNotification = new Notification(notification.title, notificationOptions);

            // Auto close after 10 seconds
            setTimeout(() => {
              browserNotification.close();
            }, 10000);

            // Handle notification click
            browserNotification.onclick = (event) => {
              event.preventDefault();
              window.focus();
              browserNotification.close();
              
              // Navigate to notifications page if needed
              const url = payload.fcmOptions?.link || payload.data?.click_action || '/notifications';
              window.location.href = url;
            };
          }
        } catch (error) {
          console.error('❌ Error handling message:', error);
        }
      });

      console.log('✅ Message listener setup successfully');
      return unsubscribe;
    } catch (error) {
      console.error('❌ Error setting up message listener:', error);
      throw error;
    }
  };

  // Handle call notifications (NO BROWSER NOTIFICATION - just in-app handling)
  const handleCallNotification = (payload) => {
    const { callerName, userType, type, channelName } = payload.data;
    
    console.log('📞 Call notification received in foreground');
    
    // Don't create browser notification for calls in foreground
    // The IncomingCallContext will handle showing the modal
    // Just increment unread count
    setUnreadCount(current => {
      const newCount = current + 1;
      localStorage.setItem('fcm_unread_count', newCount.toString());
      return newCount;
    });
  };

  const markAllAsRead = useCallback(() => {
    setUnreadCount(0);
    setMessages(prev => prev.map(msg => ({ ...msg, read: true })));
    localStorage.setItem('fcm_unread_count', '0');
    console.log('✅ All notifications marked as read');
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setUnreadCount(0);
    localStorage.setItem('fcm_unread_count', '0');
    console.log('🧹 All messages cleared');
  }, []);

  const markAsRead = useCallback((messageId) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, read: true } : msg
    ));
    setUnreadCount(prev => Math.max(0, prev - 1));
    console.log('✅ Message marked as read:', messageId);
  }, []);

  const contextValue = {
    messages,
    unreadCount,
    markAllAsRead,
    clearMessages,
    markAsRead,
    initError,
    isInitialized,
    fcmToken
  };

  return (
    <FirebaseMessagingContext.Provider value={contextValue}>
      {children}
    </FirebaseMessagingContext.Provider>
  );
};

// Custom hook for easy context usage
export const useFirebaseMessaging = () => {
  const context = useContext(FirebaseMessagingContext);
  if (!context) {
    throw new Error('useFirebaseMessaging must be used within FirebaseMessagingProvider');
  }
  return context;
};